import classNames from "classnames";
import { ReactNode } from "react";
import styles from "./style.module.scss";

export interface PillOption<T = string> {
  key: T;
  label?: string;
  /** Icon-only variant used by the funnel view switcher. */
  icon?: ReactNode;
  title?: string;
}

interface SegmentedPillsProps<T = string> {
  options: PillOption<T>[];
  value: T;
  onChange: (key: T) => void;
  /**
   * "pill" = the rounded date presets and band filters,
   * "square" = the funnel's icon view-switcher,
   * "box" = the boxed light-blue toggle above Top books.
   */
  shape?: "pill" | "square" | "box";
  size?: "sm" | "md";
  className?: string;
}

/** The grouped toggle used for date presets, band filters and view switchers. */
function SegmentedPills<T extends string | number = string>({
  options,
  value,
  onChange,
  shape = "pill",
  size = "md",
  className,
}: SegmentedPillsProps<T>) {
  return (
    <div
      role="tablist"
      className={classNames(styles.group, styles[shape], className)}
    >
      {options.map((option) => {
        const active = option.key === value;

        return (
          <button
            key={String(option.key)}
            type="button"
            role="tab"
            aria-selected={active}
            title={option.title}
            onClick={() => onChange(option.key)}
            className={classNames(
              styles.pill,
              styles[size],
              option.icon && !option.label && styles.iconOnly,
              active && styles.active
            )}
          >
            {option.icon}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedPills;
